import type { ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const badge = cva(
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-(length:--text-2xs) " +
    "font-medium leading-none",
  {
    variants: {
      tone: {
        neutral: "bg-(--surface-sunken) text-(--text-muted) ring-1 ring-inset ring-(--border)",
        brand: "bg-(--brand-subtle) text-(--brand-strong)",
        ok: "bg-(--ok-subtle) text-(--ok)",
        warn: "bg-(--warn-subtle) text-(--warn)",
        danger: "bg-(--danger-subtle) text-(--danger)",
        info: "bg-(--info-subtle) text-(--info)",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
);

export interface BadgeProps extends VariantProps<typeof badge> {
  children: ReactNode;
  className?: string;
  /** Accessible name override — for a badge whose visible text is a bare number (a count), where the
   *  number alone reads as nothing to a screen reader without the noun it's counting. */
  "aria-label"?: string;
}

/** Small inline label for counts/categories. NOT a status indicator (use StatusPill for state). */
export function Badge({ tone, children, className, ...aria }: BadgeProps) {
  return (
    <span className={cn(badge({ tone }), className)} aria-label={aria["aria-label"]}>
      {children}
    </span>
  );
}
